import { backend } from './backend';
import type { Identity } from '@icp-sdk/core/agent';
import type { IDL } from '@icp-sdk/core/candid';
import type { _SERVICE as Backend } from '../bindings/backend/declarations/backend.did';

export type ActivitySnapshot = Awaited<ReturnType<Backend['analytics_snapshot']>>;
export type AnalyticsTouch = Parameters<Backend['analytics_touch']>[0];
export type AccountProvider = Parameters<Backend['analytics_account_seen']>[0];
export type AnalyticsAccountService = Pick<Backend, 'analytics_account_seen'>;
export const analyticsAccountIdl: IDL.InterfaceFactory = ({ IDL }) => IDL.Service({ analytics_account_seen: IDL.Func([IDL.Variant({ internet_identity: IDL.Null, plug: IDL.Null })], [IDL.Variant({ ok: IDL.Null, err: IDL.Text })], []) });
let anonymous: ReturnType<typeof backend> | undefined;
export function analyticsBackend() {
  return anonymous ??= backend().catch(error => { anonymous = undefined; throw error; });
}
export async function recordAnalyticsAccount(identity: Identity) {
  if (identity.getPrincipal().isAnonymous()) return;
  try { const result = await (await backend(identity)).analytics_account_seen({ internet_identity: null }); if ('ok' in result) window.dispatchEvent(new Event('soonscreener:analytics-updated')); }
  catch { /* Activity reporting must never interrupt a sign-in or wallet action. */ }
}

const VISITOR_KEY = 'soonscreener.analytics.visitor';
const SESSION_KEY = 'soonscreener.analytics.session';
const HEX = /^(?!0{32}$)[a-f0-9]{32}$/;
const MAX_SEQUENCE = 1_000_000_000n;
let memoryVisitor: string | undefined;
let memorySession: { id: string; sequence: string } | undefined;
function randomId() {
  const bytes = new Uint8Array(16); crypto.getRandomValues(bytes);
  return [...bytes].map(value => value.toString(16).padStart(2, '0')).join('');
}
function visitorId() {
  if (memoryVisitor) return memoryVisitor;
  try {
    const saved = localStorage.getItem(VISITOR_KEY);
    if (saved && HEX.test(saved)) return memoryVisitor = saved;
    const created = randomId(); localStorage.setItem(VISITOR_KEY, created);
    // Read back a concurrently created value from another tab, if present.
    const stored = localStorage.getItem(VISITOR_KEY);
    return memoryVisitor = stored && HEX.test(stored) ? stored : created;
  } catch { return memoryVisitor = randomId(); }
}
function session() {
  if (memorySession) return memorySession;
  try {
    const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? 'null');
    if (saved && HEX.test(saved.id) && /^\d+$/.test(saved.sequence) && BigInt(saved.sequence) <= MAX_SEQUENCE) return memorySession = saved;
  } catch { /* Optional browser preference; an in-memory session is sufficient. */ }
  return memorySession = { id: randomId(), sequence: '0' };
}
function saveSession(value: { id: string; sequence: string }) {
  memorySession = value;
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(value)); } catch { /* In-memory fallback. */ }
}

/** No URL, query string, IP address, wallet balance or fingerprint is sent. */
export function nextAnalyticsTouch(navigation: boolean): AnalyticsTouch {
  let current = session();
  if (navigation || current.sequence === '0') {
    const sequence = BigInt(current.sequence) + 1n;
    current = sequence > MAX_SEQUENCE ? { id: randomId(), sequence: '1' } : { ...current, sequence: sequence.toString() };
    saveSession(current);
  }
  return { visitor_id: visitorId(), session_id: current.id, sequence: BigInt(current.sequence), visit: navigation };
}
